import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import { api } from "../utils/api";

const DEFAULT_CRON_CONFIG = { type: "daily", time: "12:00", days: [1, 2, 3, 4, 5] };
const DEFAULT_FORM_DATA = {
  name: "",
  eshop_id: "",
  schedule_type: "manual",
  schedule_expression: "",
  is_active: true,
  config: "{}",
};

// Helper to parse cron expression
function parseCronExpression(expression) {
  if (!expression || !expression.includes(" ")) return DEFAULT_CRON_CONFIG;
  const parts = expression.split(" ");
  if (parts.length !== 5) return DEFAULT_CRON_CONFIG;

  const minute = parts[0].padStart(2, "0");
  const hour = parts[1].padStart(2, "0");
  const dom = parts[2];
  const month = parts[3];
  const dow = parts[4];

  if (dom === "*" && month === "*" && dow === "*") {
    return { type: "daily", time: `${hour}:${minute}`, days: [1, 2, 3, 4, 5] };
  } else if (dom === "*" && month === "*" && dow !== "*") {
    const days = dow === "*" ? [0, 1, 2, 3, 4, 5, 6] : dow.split(",").map(Number);
    return { type: "weekly", time: `${hour}:${minute}`, days };
  }

  return { type: "custom", time: `${hour}:${minute}`, days: [1, 2, 3, 4, 5] };
}

function generateCronExpression(config, rawExpression) {
  const [hour, minute] = config.time.split(":").map((s) => parseInt(s, 10));
  if (config.type === "daily") {
    return `${minute} ${hour} * * *`;
  } else if (config.type === "weekly") {
    const days = config.days.sort().join(",");
    return `${minute} ${hour} * * ${days}`;
  }
  return rawExpression; // Return raw expression for custom type
}

function getScheduleTypeFromExpression(expression) {
  if (!expression) return "manual";
  // Check if it's an interval expression (e.g., "5m", "1h", "30s")
  if (/^\d+[smhd]$/i.test(expression)) return "interval";
  // Otherwise assume it's a cron expression
  return "cron";
}

export function getScheduleTypeLabel(type) {
  const labels = { manual: "Manual", interval: "Interval", cron: "Cron" };
  return labels[type] || type;
}

export function getStatusColor(status) {
  switch (status) {
    case "active":
      return "success";
    case "inactive":
      return "default";
    case "running":
      return "info";
    case "error":
      return "error";
    default:
      return "default";
  }
}

export { getScheduleTypeFromExpression, generateCronExpression };

export function useJobManagement() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [selectedJobIds, setSelectedJobIds] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [cronConfig, setCronConfig] = useState(DEFAULT_CRON_CONFIG);
  const [formData, setFormData] = useState(DEFAULT_FORM_DATA);

  const { data: jobs, isLoading: jobsLoading, error: jobsError } = useQuery(
    "jobs",
    () => api.get("/api/jobs").then((res) => res.data)
  );

  const { data: eshops, isLoading: eshopsLoading } = useQuery(
    "eshops",
    () => api.get("/api/eshops").then((res) => res.data)
  );

  const createJobMutation = useMutation((jobData) => api.post("/api/jobs", jobData), {
    onSuccess: () => {
      queryClient.invalidateQueries("jobs");
      handleCloseDialog();
    },
  });

  const updateJobMutation = useMutation(
    ({ id, ...jobData }) => api.put(`/api/jobs/${id}`, jobData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("jobs");
        handleCloseDialog();
      },
    }
  );

  const deleteJobMutation = useMutation((id) => api.delete(`/api/jobs/${id}`), {
    onSuccess: () => {
      queryClient.invalidateQueries("jobs");
    },
  });

  const executeJobMutation = useMutation((id) => api.post(`/api/jobs/${id}/execute`), {
    onSuccess: () => {
      queryClient.invalidateQueries("jobs");
      setSnackbar({
        open: true,
        message: "Job started successfully! Redirecting to monitoring...",
        severity: "success",
      });
      // Navigate to monitoring page after a short delay
      setTimeout(() => {
        navigate("/monitoring");
      }, 1500);
    },
    onError: (error) => {
      setSnackbar({ open: true, message: `Failed to start job: ${error.message}`, severity: "error" });
    },
  });

  const bulkExecuteMutation = useMutation(
    (jobIds) => api.post("/api/jobs/bulk-execute", { job_ids: jobIds }),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries("jobs");
        const results = data.data.results;
        const started = results.filter((r) => r.status === "started");
        const failed = results.filter((r) => r.status !== "started");

        let message = `Started ${started.length} jobs: ${started.map((r) => r.job_name).join(", ")}`;
        if (failed.length > 0) {
          message += `. Failed ${failed.length} jobs.`;
        }

        setSnackbar({ open: true, message, severity: failed.length > 0 ? "warning" : "success" });
        setSelectedJobIds([]);
      },
      onError: (error) => {
        setSnackbar({ open: true, message: `Failed to start jobs: ${error.message}`, severity: "error" });
      },
    }
  );

  const toggleJobMutation = useMutation(
    ({ id, enable }) => (enable ? api.post(`/api/jobs/${id}/enable`) : api.post(`/api/jobs/${id}/disable`)),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("jobs");
      },
    }
  );

  const handleOpenDialog = (job = null) => {
    if (job) {
      const type = getScheduleTypeFromExpression(job.schedule_expression);
      setSelectedJob(job);
      setFormData({
        name: job.job_name,
        eshop_id: job.eshop_id,
        schedule_type: type,
        schedule_expression: job.schedule_expression || "",
        is_active: job.enabled,
        config: "{}",
      });
      setCronConfig(type === "cron" ? parseCronExpression(job.schedule_expression) : DEFAULT_CRON_CONFIG);
    } else {
      setSelectedJob(null);
      setFormData(DEFAULT_FORM_DATA);
      setCronConfig(DEFAULT_CRON_CONFIG);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedJob(null);
  };

  const handleSubmit = () => {
    let expression = formData.schedule_expression;
    if (formData.schedule_type === "manual") {
      expression = null;
    } else if (formData.schedule_type === "cron" && cronConfig.type !== "custom") {
      expression = generateCronExpression(cronConfig, formData.schedule_expression);
    }

    const jobData = {
      job_name: formData.name,
      eshop_id: formData.eshop_id,
      schedule_expression: expression,
      enabled: formData.is_active,
    };

    if (selectedJob) {
      updateJobMutation.mutate({ id: selectedJob.id, ...jobData });
    } else {
      createJobMutation.mutate(jobData);
    }
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this job?")) {
      deleteJobMutation.mutate(id);
    }
  };

  const handleExecute = (id) => {
    setSnackbar({ open: true, message: "Starting job...", severity: "info" });
    executeJobMutation.mutate(id);
  };

  const handleBulkExecute = () => {
    if (selectedJobIds.length === 0) return;
    if (window.confirm(`Are you sure you want to run ${selectedJobIds.length} jobs?`)) {
      bulkExecuteMutation.mutate(selectedJobIds);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleToggle = (job) => {
    toggleJobMutation.mutate({ id: job.id, enable: !job.enabled });
  };

  return {
    // Data
    jobs,
    jobsLoading,
    jobsError,
    eshops,
    eshopsLoading,
    // Dialog / form state
    openDialog,
    selectedJob,
    formData,
    setFormData,
    cronConfig,
    setCronConfig,
    // Selection
    selectedJobIds,
    setSelectedJobIds,
    // Snackbar
    snackbar,
    handleCloseSnackbar,
    // Mutations (exposed for isLoading flags in the UI)
    createJobMutation,
    updateJobMutation,
    executeJobMutation,
    bulkExecuteMutation,
    // Handlers
    handleOpenDialog,
    handleCloseDialog,
    handleSubmit,
    handleDelete,
    handleExecute,
    handleBulkExecute,
    handleToggle,
  };
}
